<template>
  <modal :show.sync="visible" @update:show="handleModalClose" class="dark-modal">
      <div class="modal-box">
        <div class="modal-box-header">
          <h2 class="modal-title">{{ $t('users.filter.title') }}</h2>
        </div>

        <div class="forwarder-filter-content">
          <!-- Status Section -->
          <div class="field forwarder-filter-section">
            <label class="label forwarder-filter-section-label">{{ $t('status.status') }}</label>
            <div class="field is-grouped is-grouped-multiline forwarder-filter-options">
              <div class="control">
                <label class="checkbox">
                  <input 
                    v-model="localFilters.statuses" 
                    type="checkbox" 
                    value="active"
                    @change="updateResultCount"
                  >
                  {{ $t('active') }}
                </label>
              </div>
              <div class="control">
                <label class="checkbox">
                  <input 
                    v-model="localFilters.statuses" 
                    type="checkbox" 
                    value="inactive"
                    @change="updateResultCount"
                  >
                  {{ $t('inactive') }}
                </label>
              </div>
            </div>
          </div>

          <!-- Modules Section -->
          <div class="field forwarder-filter-section">
            <label class="label forwarder-filter-section-label">{{ $t('activeModules') }}</label>
            <div class="field is-grouped is-grouped-multiline forwarder-filter-options">
              <div class="control">
                <label class="checkbox">
                  <input 
                    v-model="localFilters.modules" 
                    type="checkbox" 
                    value="system_log"
                    @change="updateResultCount"
                  >
                  {{ $t('forwarders.systemLog') }}
                </label>
              </div>
              <div class="control">
                <label class="checkbox">
                  <input 
                    v-model="localFilters.modules" 
                    type="checkbox" 
                    value="resource_monitor"
                    @change="updateResultCount"
                  >
                  {{ $t('forwarders.resourceMonitor') }}
                </label>
              </div>
              <div class="control">
                <label class="checkbox">
                  <input 
                    v-model="localFilters.modules" 
                    type="checkbox" 
                    value="file_log"
                    @change="updateResultCount"
                  >
                  {{ $t('forwarders.fileLog') }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="button cancel-button"
            @click="clearAllFilters"
          >
            {{ $t('users.filter.clearAll') }}
          </button>
          <button
            type="button"
            class="button is-primary"
            @click="confirmFilters"
            :disabled="isLoading"
            :class="{ 'is-loading': isLoading }"
          >
            {{ $t('users.filter.confirm') }}
            <span v-if="resultCount !== null" class="tag is-light ml-2">
              {{ resultCount }}
            </span>
          </button>
        </div>
      </div>
  </modal>
</template>

<script>
import * as icons from 'octicons-vue'
import { Modal } from '@cyradar/ui'
import { debounce } from '@/utils'

export default {
  name: 'ForwarderFilter',
  components: {
    Modal
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => ({
        statuses: [],
        modules: []
      })
    }
  },
  data() {
    return {
      visible: false,
      localFilters: {
        statuses: [...this.filters.statuses],
        modules: [...this.filters.modules]
      },
      resultCount: null,
      isLoading: false
    }
  },
  computed: {
    filterIcon() {
      return icons.settings
    },
    filterCount() {
      return this.localFilters.statuses.length + this.localFilters.modules.length
    },
    hasActiveFilters() {
      return this.filterCount > 0
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(newValue) {
        this.visible = newValue
        if (newValue) {
          this.initializeFilters()
        }
      }
    },
    filters: {
      handler(newFilters) {
        this.localFilters = {
          statuses: [...newFilters.statuses],
          modules: [...newFilters.modules]
        }
      },
      deep: true
    }
  },
  created() {
    // Create debounced function for result count updates
    this.debouncedUpdateResultCount = debounce(() => {
      this.fetchResultCount()
    }, 500)
  },
  methods: {
    initializeFilters() {
      this.localFilters = {
        statuses: [...this.filters.statuses],
        modules: [...this.filters.modules]
      }
      this.updateResultCount()
    },
    handleModalClose(value) {
      this.$emit('update:show', value)
    },
    updateResultCount() {
      if (this.visible) {
        this.debouncedUpdateResultCount()
      }
    },
    async fetchResultCount() {
      // If no filters are active, reset count and return
      if (!this.hasActiveFilters) {
        this.resultCount = null
        return
      }

      this.isLoading = true
      try {
        const response = await this.$http.get('/api/v1/forwarders', {
          params: {
            statuses: this.localFilters.statuses.length > 0 ? this.localFilters.statuses.join(',') : undefined,
            modules: this.localFilters.modules.length > 0 ? this.localFilters.modules.join(',') : undefined
          }
        })

        if (response && response.data && response.data.data) {
          this.resultCount = response.data.data.total || 0
        }
      } catch (error) {
        console.error('Error fetching result count:', error)
        this.resultCount = 0
      } finally {
        this.isLoading = false
      }
    },
    confirmFilters() {
      this.$emit('apply-filters', {
        statuses: [...this.localFilters.statuses],
        modules: [...this.localFilters.modules],
        resultCount: this.resultCount
      })
      this.$nextTick(() => {
        this.handleModalClose(false)
      })
    },
    clearAllFilters() {
      this.localFilters = {
        statuses: [],
        modules: []
      }
      this.resultCount = null
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/dark-modal.scss';

.forwarder-filter-content {
  min-height: 150px;
  padding: 1rem 0;
}

/* Section styling for better visual separation */
.forwarder-filter-section {
  margin-bottom: 1.5rem;
}

.forwarder-filter-section:last-child {
  margin-bottom: 0.75rem;
}

/* Section label styling with improved spacing */
.forwarder-filter-section-label {
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem !important;
  color: $modal-text;
}

/* Options container with proper spacing */
.forwarder-filter-options {
  margin-top: 0.5rem;
}

/* Checkbox styling for better alignment and spacing */
.checkbox {
  margin-right: 1rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  color: $modal-text;
}

.checkbox input[type="checkbox"] {
  margin-right: 0.5rem;
}

/* Utility class for spacing */
.tag.ml-2 {
  margin-left: 0.5rem;
}
</style>
